import { motion } from 'framer-motion';
import { Database, Radio, Share2, ShoppingCart, Palette, Monitor, Tv, Headphones, ArrowUpRight } from 'lucide-react';

const services = [
  {
    icon: Database,
    title: 'Inteligencia de Datos',
    description: 'DMP, segmentación contextual y modelos de intención para convertir impresiones en revenue real.',
    accent: 'gold',
  },
  {
    icon: ShoppingCart,
    title: 'Compra Directa y Programática',
    description: 'Direct deals con publishers premium + programmatic buying. Optimización de inventario y eficiencia de inversión con performance medible.',
    accent: 'burgundy',
  },
  {
    icon: Share2,
    title: 'Social Media',
    description: 'Maquinaria de ventas de primera clase. Convertimos redes en territorio comercial activo.',
    accent: 'gold',
    href: '/social-media',
  },
  {
    icon: Radio,
    title: 'Performance Marketing',
    description: 'Solo importan los números: CPA, CPL, CPI y ROAS optimizados con modelos predictivos.',
    accent: 'burgundy',
  },
  {
    icon: Palette,
    title: 'Arsenal Creativo',
    description: 'Display, Video, Native, Rich Media y Gaming. Formatos que cortan ruido y generan acción.',
    accent: 'gold',
  },
  {
    icon: Monitor,
    title: 'DOOH',
    description: 'Pantallas premium en ubicaciones estratégicas. Targeting contextual urbano y métricas reales de impacto.',
    accent: 'burgundy',
  },
  {
    icon: Tv,
    title: 'Connected TV',
    description: 'El prime time es tuyo. Inventory premium en CTV con frecuencia controlada y medición cross-device.',
    accent: 'gold',
  },
  {
    icon: Headphones,
    title: 'Audio Digital',
    description: 'Podcasts, programmatic audio y CTV audio. Atención sin distracciones, conversión sin fricción.',
    accent: 'burgundy',
  },
];

export const ServicesSection = () => {
  return (
    <section id="servicios" className="relative py-32 px-6 lg:px-12 bg-[#0A0A0A] overflow-hidden">
      {/* Subtle grid */}
      <div className="absolute inset-0 grid-bg opacity-30" />
      
      {/* Decorative gradient orbs */}
      <div className="absolute top-40 right-0 w-[500px] h-[500px] rounded-full opacity-20" style={{
        background: 'radial-gradient(circle, rgba(184, 146, 63, 0.3) 0%, transparent 70%)'
      }} />
      
      <div className="max-w-7xl mx-auto relative">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="mb-20 max-w-3xl"
        >
          <div className="font-mono text-gold text-xs tracking-[0.3em] uppercase mb-6 flex items-center gap-3">
            <span className="w-12 h-px bg-gold" />
            Servicios
          </div>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-light tracking-tight mb-6 leading-[1.05] text-white">
            Tu arsenal para
            <br />
            <span className="italic font-medium" style={{ color: '#C56875' }}>dominar el mercado</span>
            <span className="text-gold">.</span>
          </h2>
          <p className="font-sans text-lg text-white/50 max-w-2xl leading-relaxed">
            Soluciones 360° que eliminan fronteras y convierten tu marca en líder de su categoría.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {services.map((service, index) => {
            const isGold = service.accent === 'gold';
            const Card = service.href ? motion.a : motion.div;
            return (
              <Card
                href={service.href}
                key={index}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.08 }}
                whileHover={{ y: -6 }}
                className="group relative dark-card dark-card-hover rounded-2xl p-8 transition-all duration-500 overflow-hidden block"
                data-testid={`service-card-${index}`}
              >
                {/* Hover gradient */}
                <div className={`absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 ${
                  isGold 
                    ? 'bg-gradient-to-br from-gold/[0.08] via-transparent to-transparent'
                    : 'bg-gradient-to-br from-burgundy/[0.12] via-transparent to-transparent'
                }`} />
                
                {/* Corner accent */}
                <div className={`absolute top-0 right-0 w-16 h-16 opacity-0 group-hover:opacity-100 transition-opacity duration-500 ${
                  isGold ? 'bg-gold/10' : 'bg-burgundy/15'
                } rounded-bl-full`} />
                
                <div className="relative z-10">
                  <div className={`inline-flex w-11 h-11 rounded-xl items-center justify-center mb-6 transition-all duration-300 ${
                    isGold
                      ? 'bg-gold/10 group-hover:bg-gold group-hover:shadow-lg group-hover:shadow-gold/40'
                      : 'bg-burgundy/15 group-hover:bg-burgundy group-hover:shadow-lg group-hover:shadow-burgundy/40'
                  }`}>
                    <service.icon className={`w-5 h-5 transition-colors ${
                      isGold ? 'text-gold group-hover:text-black' : 'text-burgundy-300 group-hover:text-white'
                    }`} />
                  </div>
                  
                  <h3 className="font-display text-xl font-medium tracking-tight text-white mb-3 leading-snug">
                    {service.title}
                  </h3>
                  
                  <p className="font-sans text-sm text-white/50 leading-relaxed mb-6">
                    {service.description}
                  </p>
                  
                  {service.href && (
                    <div className={`inline-flex items-center gap-1 font-mono text-[10px] tracking-widest uppercase transition-colors ${
                      isGold ? 'text-gold' : 'text-burgundy-300'
                    }`}>
                      <span>Ver servicio</span>
                      <ArrowUpRight className="w-3 h-3 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                    </div>
                  )}
                </div>
              </Card>
            );
          })}
        </div>
      </div>
    </section>
  );
};
